import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { setMusicState } from "../../state/features/musicSlice";

// Shared audio instance
const sharedAudio = typeof window !== 'undefined' ? new Audio() : null;
if (sharedAudio) {
  sharedAudio.loop = true;
}

const AudioToggle = ({ showToggle, music }: any) => {
  const { isMusicPlaying } = useSelector((state: RootState) => state.music);
  const dispatch = useDispatch();
  const hasAttemptedInitialPlay = useRef<boolean>(false);
  const userHasInteracted = useRef<boolean>(false);
  
  // Initialize state from localStorage
  const [isPlaying, setIsPlaying] = useState<boolean>(() => {
    const stored = localStorage.getItem("isMusicPlaying");
    return stored ? JSON.parse(stored) : true; // Default to true (unmuted)
  });

  // Initialize Redux state immediately on mount
  useEffect(() => {
    const storedMusicState = localStorage.getItem("isMusicPlaying");
    const initialState = storedMusicState ? JSON.parse(storedMusicState) : true;
    
    // Sync Redux state immediately
    dispatch(setMusicState({ isMusicPlaying: initialState }));
    setIsPlaying(initialState);
  }, [dispatch]);

  // Update audio src when music prop changes
  useEffect(() => {
    if (sharedAudio && music && sharedAudio.src !== music) {
      sharedAudio.src = music;
    }
  }, [music]);

  // Toggle music state
  const toggleMusic = () => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);
    localStorage.setItem("isMusicPlaying", JSON.stringify(newPlayingState));
    dispatch(setMusicState({ isMusicPlaying: newPlayingState }));

    // This is definitely a user interaction, so mark it as such
    userHasInteracted.current = true;

    // Play or pause audio directly
    if (sharedAudio) {
      if (newPlayingState) {
        sharedAudio.play().catch((error) => {
          console.log("Manual play failed:", error);
          // If manual play fails, show user a message or handle gracefully
        });
      } else {
        sharedAudio.pause();
      }
    }
  };

  // Handle initial audio setup after first user interaction
  useEffect(() => {
    if (!hasAttemptedInitialPlay.current) {
      hasAttemptedInitialPlay.current = true;

      if (sharedAudio && !isPlaying) {
        sharedAudio.pause();
      }
    }
  }, [isPlaying]);

  // Sync shared audio when Redux music state changes (after user interaction)
  useEffect(() => {
    if (sharedAudio && userHasInteracted.current) {
      if (isMusicPlaying) {
        // Only attempt to play if user has already interacted
        sharedAudio.play().catch((error) => {
          console.log("Play failed on Redux state change:", error);
        });
      } else {
        sharedAudio.pause();
      }
    }
  }, [isMusicPlaying]);

  // Enhanced user interaction handling for autoplay
  useEffect(() => {
    const enableAudio = (event: Event) => {
      // Ensure this is a genuine user interaction
      if (!userHasInteracted.current && event.isTrusted) {
        userHasInteracted.current = true;
        
        // If music should be playing, start it now
        if (isPlaying && sharedAudio) {
          // Small delay to ensure the interaction is fully processed
          setTimeout(() => {
            sharedAudio.play().catch((error) => {
              console.log("Play after user interaction failed:", error);
              // Reset the flag so we can try again on next interaction
              userHasInteracted.current = false;
            });
          }, 100);
        }
        
        // Remove all listeners after first successful interaction
        removeAllListeners();
      }
    };

    const removeAllListeners = () => {
      // Mouse events (most reliable for desktop)
      document.removeEventListener("click", enableAudio, true);
      document.removeEventListener("mousedown", enableAudio, true);
      
      // Touch events (most reliable for mobile)
      document.removeEventListener("touchstart", enableAudio, true);
      document.removeEventListener("touchend", enableAudio, true);
      
      // Keyboard events
      document.removeEventListener("keydown", enableAudio, true);
      
      // Less reliable interactions - remove these
      document.removeEventListener("mouseup", enableAudio, true);
      document.removeEventListener("touchmove", enableAudio, true);
      document.removeEventListener("keyup", enableAudio, true);
      document.removeEventListener("scroll", enableAudio, true);
      document.removeEventListener("wheel", enableAudio, true);
      document.removeEventListener("contextmenu", enableAudio, true);
    };

    // Always add listeners when component mounts (if user hasn't interacted yet)
    if (!userHasInteracted.current) {
      // Primary interactions (most reliable) - use capture phase
      document.addEventListener("click", enableAudio, { capture: true });
      document.addEventListener("mousedown", enableAudio, { capture: true });
      
      // Touch events (mobile)
      document.addEventListener("touchstart", enableAudio, { capture: true });
      document.addEventListener("touchend", enableAudio, { capture: true });
      
      // Keyboard events
      document.addEventListener("keydown", enableAudio, { capture: true });
      
      // Secondary interactions (less reliable but worth trying)
      document.addEventListener("mouseup", enableAudio, { capture: true });
      document.addEventListener("touchmove", enableAudio, { capture: true, passive: true });
      document.addEventListener("keyup", enableAudio, { capture: true });
      document.addEventListener("scroll", enableAudio, { capture: true, passive: true });
      document.addEventListener("wheel", enableAudio, { capture: true, passive: true });
      document.addEventListener("contextmenu", enableAudio, { capture: true });
    }

    return removeAllListeners;
  }, []); // Empty dependency array - only run once on mount

  return (
    <div className="AudioToggleButton">
      {showToggle && (
        <>
          <input
            type="checkbox"
            id="checkboxInput"
            checked={isPlaying}
            onChange={toggleMusic}
          />
          <label
            htmlFor="checkboxInput"
            className="toggleSwitch"
            title={isPlaying ? "Stop music" : "Play music"}
          >
            {isMusicPlaying ? (
              <div className="mute-speaker" style={{ opacity: "100" }}>
                <svg viewBox="0 0 75 75">
                  <path
                    d="M39.389,13.769 L22.235,28.606 L6,28.606 L6,47.699 L21.989,47.699 L39.389,62.75 L39.389,13.769z"
                    style={{
                      stroke: "#fff",
                      strokeWidth: 5,
                      strokeLinejoin: "round",
                      fill: "#fff",
                    }}
                  />
                  <path
                    d="M48,27.6a19.5,19.5 0 0 1 0,21.4M55.1,20.5a30,30 0 0 1 0,35.6M61.6,14a38.8,38.8 0 0 1 0,48.6"
                    style={{
                      fill: "none",
                      stroke: "#fff",
                      strokeWidth: 5,
                      strokeLinecap: "round",
                    }}
                  />
                </svg>
              </div>
            ) : (
              <div className="speaker" style={{ opacity: "100" }}>
                <svg viewBox="0 0 75 75" stroke="#fff" strokeWidth={5}>
                  <path
                    d="m39,14-17,15H6V48H22l17,15z"
                    fill="#fff"
                    strokeLinejoin="round"
                  />
                  <path
                    d="m49,26 20,24m0-24-20,24"
                    fill="#fff"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            )}
          </label>
        </>
      )}
    </div>
  );
};

export default AudioToggle;